/**
 * GoogieInvoices — the money page.
 *
 * INVOICE-1. Rennan asked for invoicing to be rebuilt; surveying it
 * first found that it already existed twice — a legacy per-project
 * `invoices` table holding a number and an uploaded PDF, and the
 * Document Builder, where an invoice is a real document with line
 * items, deposits, a public page and Stripe Connect behind it.
 *
 * What did NOT exist was any view across them. `/billing/:projectId`
 * answers "what did this project invoice". No screen answered the
 * question a photographer actually asks, which is "who hasn't paid
 * me". So this is the gap rather than a redesign of what was there.
 *
 * THE FOUR NUMBERS COME FIRST because they are the reason to open the
 * page, and OUTSTANDING is the one worth getting right: it is what is
 * still owed, not the face value of unpaid invoices. A 4,800 shoot
 * with a 2,400 deposit paid is 2,400 outstanding, and a page that
 * says 4,800 is telling a studio they are owed money they have
 * already been given. The arithmetic is in `invoiceTotals` with its
 * own spec.
 *
 * Overdue is the only filled tile. It is the one that costs money to
 * ignore, and if everything shouts then nothing does.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, Banknote, CalendarClock, Plus, Wallet } from 'lucide-react';
import { G } from '@/lib/googie';
import { useClients } from '@/services/clients';
import { money, totalsFor, useInvoicing, type InvoiceRow, type InvoiceState } from '@/services/invoicing';
import { GoogieScreen } from '../GoogieShell';
import {
  GoogieAvatar,
  GoogieCapsule,
  GoogieChip,
  GoogieHeader,
  GoogieMasthead,
  GoogiePage,
  GoogieStat,
} from '../primitives';
import { EmptyState } from '@/components/shared/EmptyState';
// NOVA-LOAD-1 — `OrbitLoader`, not `NovaSpinner`. The spinner is a
// deliberate 16px CROP of Nova's antenna for buttons and table cells,
// and at card size it reads as the wrong character (Rennan: "its the
// wrong 2d nova"). OrbitLoader is the canonical 2026 kit avatar,
// floating on the blue glow, with the rainbow eye-build.
import OrbitLoader from '@/components/shared/OrbitLoader';
import { cn } from '@/lib/utils';

/** The lenses, and what each one is for. */
const LENSES = [
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'paid', label: 'Paid' },
  { id: 'drafts', label: 'Drafts' },
  { id: 'all', label: 'All' },
] as const;
type Lens = (typeof LENSES)[number]['id'];

const STATE_LABEL: Record<InvoiceState, string> = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
};

/** One hue per state, and only one of them alarming. */
const STATE_TONE: Record<InvoiceState, string> = {
  draft: G.sky,
  sent: G.cyan,
  part_paid: G.mustard,
  paid: G.lime,
  overdue: G.red,
};

export function GoogieInvoices() {
  const { invoices, hasDueDates, isLoading } = useInvoicing();
  const { data: clients = [] } = useClients();
  const [lens, setLens] = useState<Lens>('outstanding');

  const named = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clients as Array<{ id?: string; name?: string }>) {
      if (c.id) byId.set(c.id, c.name ?? 'Client');
    }
    return invoices.map((i) => ({ ...i, clientName: i.clientId ? byId.get(i.clientId) ?? null : null }));
  }, [clients, invoices]);

  const totals = useMemo(() => totalsFor(named), [named]);

  const shown = useMemo(() => {
    const rows = named.filter((i) => {
      if (lens === 'all') return true;
      if (lens === 'overdue') return i.state === 'overdue';
      if (lens === 'paid') return i.state === 'paid';
      if (lens === 'drafts') return i.state === 'draft';
      return i.state !== 'paid' && i.state !== 'draft';
    });
    // Most pressing first: the late ones, then by what is owed.
    return rows.sort((a, b) => {
      if (a.state === 'overdue' !== (b.state === 'overdue')) return a.state === 'overdue' ? -1 : 1;
      return b.dueCents - a.dueCents || b.createdAt.getTime() - a.createdAt.getTime();
    });
  }, [lens, named]);

  return (
    <GoogieScreen>
      <GoogieHeader capsule={<GoogieCapsule dot={G.lime}>Money</GoogieCapsule>} />

      <GoogiePage>
        <GoogieMasthead
          eyebrow={`${format(new Date(), 'MMMM yyyy').toUpperCase()} · MONEY`}
          eyebrowColor={G.lime}
          title="Invoices"
          actions={
            <Link to="/invoices/new" className="g-pill g-pill-solid no-underline">
              <Plus className="h-5 w-5" aria-hidden /> New invoice
            </Link>
          }
        />

        <div className="g-list mt-7 flex flex-wrap gap-4">
          <GoogieStat
            value={money(totals.outstandingCents)}
            label={
              totals.outstandingCount === 1 ? '1 invoice outstanding' : `${totals.outstandingCount} invoices outstanding`
            }
            icon={<Wallet className="h-5 w-5" style={{ color: G.cyan }} />}
            accent={G.cyan}
          />
          <GoogieStat
            value={money(totals.overdueCents)}
            label={totals.overdueCount === 1 ? '1 invoice late' : `${totals.overdueCount} invoices late`}
            icon={<AlertTriangle className="h-5 w-5" />}
            accent={G.red}
            // The one filled tile. If everything shouts, nothing does.
            filled={totals.overdueCents > 0}
          />
          <GoogieStat
            value={money(totals.dueThisMonthCents)}
            label="Due this month"
            icon={<CalendarClock className="h-5 w-5" style={{ color: G.mustard }} />}
            accent={G.mustard}
          />
          <GoogieStat
            value={money(totals.paidThisMonthCents)}
            label="Paid this month"
            icon={<Banknote className="h-5 w-5" style={{ color: G.lime }} />}
            accent={G.lime}
          />
        </div>

        {!hasDueDates && invoices.length > 0 && (
          // Said once, flatly. An "Overdue" tile that can never fill
          // is worse than one that explains itself.
          <p className="g-mute mt-4 text-[14px]">
            {/* INVOICE-COPY-1 — this used to say the migration adding
                due dates was "written and waiting". It landed (Drizzle
                0040–0045), so the only true version of this sentence is
                about these invoices, not about the schema. */}
            None of these invoices carries a due date, so only late instalments count as overdue. Set one when
            you build an invoice and this starts counting.
          </p>
        )}

        <div className="g-scroll-x mt-7 flex gap-2 pb-1">
          {LENSES.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLens(l.id)}
              aria-pressed={lens === l.id}
              className={cn('g-pill flex-none', lens === l.id && 'g-pill-solid')}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="g-list mt-4 flex flex-col gap-2.5">
          {isLoading ? (
            <div className="g-card flex items-center justify-center p-10">
              <OrbitLoader size="md" label="Counting what you’re owed" />
            </div>
          ) : shown.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={lens === 'outstanding' ? 'Nobody owes you anything' : 'Nothing here'}
              description={
                lens === 'outstanding'
                  ? 'Every invoice you have sent has been paid. Enjoy it.'
                  : 'Try another filter, or send your first invoice.'
              }
            />
          ) : (
            shown.map((i) => <InvoiceRowView key={i.id} invoice={i} />)
          )}
        </div>
      </GoogiePage>
    </GoogieScreen>
  );
}

function InvoiceRowView({ invoice }: { invoice: InvoiceRow }) {
  const tone = STATE_TONE[invoice.state];
  const when = invoice.dueOn ?? invoice.nextDueAt;
  const sub = [
    invoice.clientName,
    invoice.number ? `No. ${invoice.number}` : null,
    invoice.state === 'paid'
      ? invoice.paidAt
        ? `Paid ${format(invoice.paidAt, 'd MMM')}`
        : 'Paid'
      : when
        ? `${invoice.state === 'overdue' ? 'Was due' : 'Due'} ${format(when, 'd MMM')}`
        : 'No date set',
  ]
    .filter(Boolean)
    .join(' · ');

  const body = (
    <>
      {/* GOOGIE-TONE-1 — the 3px bar is gone. Rennan: "the state is
          encoded three times — the coloured bar on the left, the chip
          on the right, and the amount treatment. Pick one. The chip
          earns its place; the bar is redundant." The avatar takes the
          slot instead, which is what every other list in Orbit leads
          with and makes a row scannable by client rather than by
          reading it. */}
      <GoogieAvatar name={invoice.clientName || invoice.title} hue={tone} size={40} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[16px] font-semibold">{invoice.title}</span>
        <span className="g-mute block truncate text-[14.5px]">{sub}</span>
      </span>
      {/* A money column that does not line up is a money column you
          have to read instead of scan, so the amount gets a floor and
          the chip beside it gets one too (`g-chip-fixed`). */}
      <span className="min-w-[104px] flex-none text-right">
        <span className="g-display block text-[18.5px] tabular-nums">
          {/* What is still OWED, with the face value underneath when
              they differ — a part-paid invoice has two numbers and
              hiding either one is how a studio chases the wrong
              amount. */}
          {money(invoice.state === 'paid' ? invoice.totalCents : invoice.dueCents)}
        </span>
        {invoice.paidCents > 0 && invoice.state !== 'paid' && (
          <span className="g-mute block text-[13px] tabular-nums">of {money(invoice.totalCents)}</span>
        )}
      </span>
      <GoogieChip className="g-chip-fixed" tone={invoice.state === 'draft' ? undefined : tone}>
        {STATE_LABEL[invoice.state]}
      </GoogieChip>
    </>
  );

  // INVOICE-2 — straight to the invoice editor, with or without a
  // project. Contract Studio can still open the same document; this
  // is just the door shaped like the thing behind it.
  return (
    <Link to={`/invoices/${invoice.id}`} className="g-row items-center no-underline" style={{ color: 'inherit' }}>
      {body}
    </Link>
  );
}

export default GoogieInvoices;
